/**
 * Checklists Page
 * Manage checklist templates (SmartNyuki-style)
 * Route: /checklists
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import {
    Add as AddIcon,
    ContentCopy as CopyIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import SaveToolbar from '@components/common/SaveToolbar';
import ChecklistTree from '../../components/checklists/ChecklistTree';
import {
    getChecklists,
    getChecklist,
    createChecklist,
    updateChecklist,
    deleteChecklist,
    getCategoryTree,
    Checklist,
} from '@api/checklists';

const ChecklistsPage: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [checklists, setChecklists] = useState<Checklist[]>([]);
    const [activeChecklist, setActiveChecklist] = useState<Checklist | null>(null);
    const [selectedChecklistId, setSelectedChecklistId] = useState<number | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    useEffect(() => {
        loadChecklists();
    }, []);

    const loadChecklists = async () => {
        try {
            setLoading(true);
            const response = await getChecklists();
            setChecklists(response.data.checklists);

            if (response.data.checklist) {
                setSelectedChecklistId(response.data.checklist.id);
                await loadChecklist(response.data.checklist.id);
            }
        } catch (error: any) {
            setErrorMessage(error.response?.data?.message || 'Failed to load checklists');
        } finally {
            setLoading(false);
        }
    };

    const loadChecklist = async (id: number) => {
        try {
            const response = await getChecklist(id);
            setActiveChecklist(response.data);
        } catch (error: any) {
            setErrorMessage(error.response?.data?.message || 'Failed to load checklist');
        }
    };

    const handleChecklistSelect = async (id: number) => {
        setSelectedChecklistId(id);
        await loadChecklist(id);
    };

    const handleSave = async () => {
        if (!activeChecklist) return;

        try {
            setSaving(true);
            setErrorMessage('');

            if (activeChecklist.id) {
                // Update existing
                await updateChecklist(activeChecklist.id, {
                    name: activeChecklist.name,
                    description: activeChecklist.description,
                    category_ids: activeChecklist.category_ids,
                });
            } else {
                // Create new
                const response = await createChecklist({
                    name: activeChecklist.name,
                    description: activeChecklist.description,
                    category_ids: activeChecklist.category_ids,
                });
                setSelectedChecklistId(response.data.id);
            }

            await loadChecklists();
        } catch (error: any) {
            setErrorMessage(error.response?.data?.message || 'Failed to save checklist');
        } finally {
            setSaving(false);
        }
    };

    const location = useLocation();

    useEffect(() => {
        if (location.pathname === '/checklists/create' && !activeChecklist && !loading) {
            handleNewChecklist();
        }
    }, [location.pathname, loading, activeChecklist]);

    useEffect(() => {
        if (!loading && checklists.length === 0 && !activeChecklist) {
            handleNewChecklist();
        }
    }, [loading, checklists.length, activeChecklist]);

    const handleNewChecklist = async () => {
        let taxonomy = activeChecklist?.taxonomy;

        if (!taxonomy || taxonomy.length === 0) {
            try {
                const response = await getCategoryTree();
                if (response.success && response.data.length > 0) {
                    taxonomy = response.data;
                } else {
                    console.warn('Category tree is empty', response);
                    taxonomy = [];
                }
            } catch (error) {
                console.error('Failed to load category tree', error);
                taxonomy = [];
            }
        }

        const newChecklist: Checklist = {
            id: 0,
            name: `Checklist ${checklists.length + 1}`,
            description: '',
            category_ids: [],
            taxonomy: taxonomy || [],
            owner: true,
            researches: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        setActiveChecklist(newChecklist);
        setSelectedChecklistId(null);

        // Update URL if not already there
        if (location.pathname !== '/checklists/create') {
            navigate('/checklists/create');
        }
    };

    const handleDuplicate = async () => {
        if (!activeChecklist) return;

        const duplicated: Checklist = {
            ...activeChecklist,
            id: 0,
            name: `${activeChecklist.name} (copy)`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        setActiveChecklist(duplicated);
        setSelectedChecklistId(null);
    };

    const handleDelete = async () => {
        if (!activeChecklist?.id) return;

        try {
            setDeleting(true);
            await deleteChecklist(activeChecklist.id);
            setDeleteDialogOpen(false);
            await loadChecklists();
        } catch (error: any) {
            setErrorMessage(error.response?.data?.message || 'Failed to delete checklist');
        } finally {
            setDeleting(false);
        }
    };

    const handleUpdateCategories = (categoryIds: number[]) => {
        if (activeChecklist) {
            setActiveChecklist({
                ...activeChecklist,
                category_ids: categoryIds,
            });
        }
    };

    const canDelete = checklists.filter(c => c.owner).length > 1;
    const isValid = activeChecklist && activeChecklist.name.trim().length > 0;

    if (loading) {
        return (
            <Container>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={50} />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
            <SaveToolbar
                onSave={handleSave}
                saveDisabled={!isValid || saving}
                loading={saving}
                showDelete={canDelete && activeChecklist?.id !== 0}
                onDelete={() => setDeleteDialogOpen(true)}
                deleteDisabled={deleting}
            />

            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
                Checklist Templates
            </Typography>

            {errorMessage && (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    onClose={() => setErrorMessage('')}
                >
                    {errorMessage}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleNewChecklist}
                >
                    New Checklist
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={handleDuplicate}
                    disabled={!activeChecklist}
                >
                    Duplicate
                </Button>
            </Box>

            {activeChecklist && (
                <Box>
                    {checklists.length > 1 && (
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Select Checklist</InputLabel>
                            <Select
                                value={selectedChecklistId || ''}
                                label="Select Checklist"
                                onChange={(e) => handleChecklistSelect(Number(e.target.value))}
                            >
                                {checklists.map((checklist) => (
                                    <MenuItem key={checklist.id} value={checklist.id}>
                                        {checklist.name}
                                        {checklist.researches && checklist.researches.length > 0 && (
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                sx={{ ml: 1, color: 'text.secondary' }}
                                            >
                                                (Research: {checklist.researches[0]})
                                            </Typography>
                                        )}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <Box sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            variant="standard"
                            label="Name"
                            value={activeChecklist.name}
                            onChange={(e) =>
                                setActiveChecklist({ ...activeChecklist, name: e.target.value })
                            }
                            inputProps={{ maxLength: 50, style: { fontSize: '1.5rem' } }}
                            InputLabelProps={{ style: { fontSize: '1.1rem' } }}
                            helperText={`${activeChecklist.name.length}/50`}
                            disabled={!activeChecklist.owner}
                        />
                    </Box>

                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Checklist Items
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Check/uncheck the boxes in the list below to add/remove items from your hive checklist. You can also unfold/fold and drag/drop items to re-order them to your own style (N.B. this is not possible on a touchscreen device).
                    </Typography>

                    {activeChecklist.taxonomy && (
                        <ChecklistTree
                            items={activeChecklist.taxonomy}
                            selected={activeChecklist.category_ids || []}
                            disabled={!activeChecklist.owner}
                            onUpdateCategories={handleUpdateCategories}
                        />
                    )}
                </Box>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Checklist</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete "{activeChecklist?.name}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ChecklistsPage;
